import tournamentsService from "@/services/tournaments";
import matchService from "@/services/match";
import first from "lodash/first";

import { TournamentStatusHelper } from "@/components/tournament/types";

const default_state = {
  currentTurnament: {},
  currentMatch: {},
  secondMatchStatus: null,

  startedMatches: [],
  participation: [],
  checkins: [],

  protestedMatches: []
};

const getters = {
  tournament: s => s.currentTurnament,
  match: s => s.currentMatch,
  matchParticipants: ({ currentMatch }) => [
    currentMatch.player1,
    currentMatch.player2
  ],
  pickInfo: ({ currentMatch }) => currentMatch.pick_ban_info,
  picker: (state, { matchParticipants, pickInfo }) => {
    if (!pickInfo) return null;
    return matchParticipants.find(player => player.id === pickInfo.player_id);
  },
  isParticipant(state, { matchParticipants }, rootState, rootGetters) {
    const user = rootGetters["auth/user"];
    return user && matchParticipants.find(p => p && p.id === user.id);
  },
  secondMatchStatus: s => s.secondMatchStatus,
  startedMatch: s => first(s.startedMatches) || {},
  participation: s => s.participation || [],
  checkins: s => s.checkins || [],
  protests: s => s.protestedMatches || [],

  tournamentStatuses: (s, g) => {
    const status = g.tournament.status || 0;
    return {
      NotStarted: TournamentStatusHelper.checkIsNotStarted(status),
      Upcoming: TournamentStatusHelper.checkIsUpcoming(status),
      RegStarted: TournamentStatusHelper.checkIsRegStarted(status),
      RegOver: TournamentStatusHelper.checkIsRegOver(status),
      CheckIn: TournamentStatusHelper.checkIsCheckIn(status),
      GridReady: TournamentStatusHelper.checkIsGridReady(status),
      Live: TournamentStatusHelper.checkIsLive(status),
      Over: TournamentStatusHelper.checkIsOver(status)
    };
  }
};

const mutations = {
  MERGE_TOURNAMENT(state, item) {
    state.currentTurnament = Object.assign({}, state.currentTurnament, item);
  },

  MERGE_MATCH(state, item) {
    state.currentMatch = Object.assign({}, state.currentMatch, item);
  },

  CLEAR_MATCH(state) {
    state.currentMatch = {};
  },

  SET_MATCH_STATUS(state, status) {
    state.secondMatchStatus = status;
  },

  SET_STARTED_MATCHES(state, list) {
    state.startedMatches = list;
  },

  SET_TOUR_PARTICIPATION(state, list) {
    state.participation = list;
  },

  SET_TOUR_CHECKINS(state, list) {
    state.checkins = list;
  },

  SET_PROTESTED_MATCHES(state, list) {
    state.protestedMatches = list;
  },

  PICK_HERO({ currentMatch }, hero) {
    currentMatch.pick_ban_history.push(hero);
  },

  SET_PICK_INFO({ currentMatch }, pickInfo) {
    currentMatch.pick_ban_info = pickInfo;
  },

  SET_PICK_HISTORY({ currentMatch }, pickHistory) {
    currentMatch.pick_ban_history = pickHistory;
  }
};

const actions = {
  async init({ dispatch, rootGetters }) {
    this.watch(
      () =>
        rootGetters["auth/isAuthenticated"] && rootGetters["auth/isRegistred"],
      isRegistred => dispatch("processTemporaryData", isRegistred)
    );
  },

  async checkTourParticipation({ commit }) {
    const {
      participation = [],
      check_ins = []
    } = await tournamentsService.checkParticipation();

    // if (getters.isConfirmedUser) {
    commit("SET_TOUR_PARTICIPATION", participation);
    commit("SET_TOUR_CHECKINS", check_ins);
    // }
  },

  async updateTournament({ commit, rootGetters }, data = {}) {
    let { tournament_id } = rootGetters.routeParams;
    let players_count = 0;

    if (tournament_id) {
      const res = await tournamentsService.getPlayersCount(tournament_id);
      if (res.success) players_count = res.players_count;
    }

    commit("MERGE_TOURNAMENT", { ...data, players_count });
  },

  async fetchProtestedMatches({ commit }) {
    let list = await matchService.fetchProtestedMatches();
    commit("SET_PROTESTED_MATCHES", list);
  },

  async fetchStartedMatches({ commit }) {
    let list = await matchService.fetchActiveMatches();
    commit("SET_STARTED_MATCHES", list);
  },

  async fetchMatch({ dispatch }, id) {
    let match = await matchService.fetchMatch(id);
    dispatch("updateMatch", match);
  },

  async processTemporaryData({ dispatch, rootGetters }, isRegistred = false) {
    if (isRegistred || rootGetters["auth/isRegistred"]) {
      await dispatch("fetchTemporaryData");
    } else {
      await dispatch("clearTemporaryData");
    }
  },

  async fetchTemporaryData({ dispatch, rootGetters }) {
    if (rootGetters["auth/isReferee"]) {
      await dispatch("fetchProtestedMatches");
    }

    if (rootGetters["auth/isRegistred"]) {
      await dispatch("fetchStartedMatches");
      await dispatch("checkTourParticipation");
    }
  },

  socket_pickBanHero({ commit }, { current, next }) {
    commit("SET_PICK_INFO", next);
    commit("PICK_HERO", current);
  },

  async socket_resetPickBan({ getters, commit }) {
    let match = await matchService.fetchMatch(getters.match.id);
    commit("SET_PICK_INFO", match.pick_ban_info);
    commit("SET_PICK_HISTORY", match.pick_ban_history);
  },

  updateMatch({ commit, rootGetters }, match = {}) {
    let status = matchService.detectSecondStatus(
      match,
      rootGetters["auth/user"],
      rootGetters["auth/isReferee"]
    );

    commit("MERGE_MATCH", match);
    commit("SET_MATCH_STATUS", status);
  },

  clearTemporaryData({ commit }) {
    commit("SET_MATCH_STATUS", null);
    commit("SET_STARTED_MATCHES", []);
    commit("SET_TOUR_PARTICIPATION", []);
    commit("SET_TOUR_CHECKINS", []);
    commit("SET_PROTESTED_MATCHES", []);
  }
};

export default {
  namespaced: true,
  state: default_state,
  getters,
  actions,
  mutations
};
